import { Button, Grid } from "basis";
import { postSignUp } from "library/api";
import { Dispatch, FC, useState } from "react";
import { useEventTracking, usePageTracking } from "react-event-tracker";
import { useMutation } from "react-query";
import { Loading } from "../../Loading/Loading";
import { PasswordInput } from "../../PasswordInput/PasswordInput";
import { ProgressBar } from "../../ProgressBar/ProgressBar";
import { Actions, PAGES, PREVIOUS_SCREEN, SIGN_UP_ERROR } from "./actions";

interface Props {
  steps: number[];
  currentStep: number;
  dispatch: Dispatch<Actions>;
  userDetails: {
    name: string;
    email: string;
  };
  setScreen: Function;
  params: {
    currentStep: number;
    email: string;
    name: string;
    password: string;
    steps: number[];
  };
}

const CUSTOMISE_ERROR_MESSAGES = {
  BAD_REQUEST: "Invalid email. Please check your details and try again.",
};

export const CreatePasswordComponent: FC<Props> = ({
  steps,
  currentStep,
  dispatch,
  userDetails: { name, email },
  params,
  setScreen,
}) => {
  const [state, setState] = useState<{
    password: string;
    fieldError: boolean;
  }>({
    password: "",
    fieldError: false,
  });
  const { password, fieldError } = state;

  const { trackEvent } = useEventTracking();

  const { mutate, isLoading } = useMutation(postSignUp, {
    onSuccess: () => {
      setScreen("VerifyEmail", {
        ...params,
        name,
        email,
        password,
      });
    },
    onError: (error: { code: string; message: string }) => {
      dispatch({
        type: SIGN_UP_ERROR,
        payload: {
          currentScreen:
            error.code === "USER_ALREADY_EXISTS"
              ? PAGES.ALREADY_REGISTERED
              : PAGES.DETAILS,
          errorMsg: CUSTOMISE_ERROR_MESSAGES[error.code] ?? error.message,
        },
      });
    },
  });

  if (isLoading) {
    return (
      <div style={{ flex: "1 1", height: "428px" }}>
        <Loading />
      </div>
    );
  }
  return (
    <div>
      <ProgressBar steps={steps} currentStep={currentStep + 1} />
      <h1 className="h4">Create a password</h1>
      <PasswordInput
        label="Password"
        evaluator
        onChange={(e) =>
          setState({
            fieldError: !e.success,
            password: e.value.replace(/\s/g, ""),
          })
        }
      />
      <Grid cols="2" cols-sm="160px 160px" colsGap="8" margin="8 0 0 0">
        <Grid.Item colSpan="0">
          <Button
            width="100%"
            disabled={fieldError || !password}
            onClick={() => {
              mutate({
                firstName: name,
                id: email,
                password,
              });
              trackEvent({
                event: {
                  category: "application",
                  action: "application-next",
                  location: "Create password",
                  label: "Next",
                },
              });
            }}
          >
            Next
          </Button>
        </Grid.Item>
        <Grid.Item colSpan="1">
          <Button
            variant="secondary"
            onClick={() => {
              dispatch({
                type: PREVIOUS_SCREEN,
                payload: {
                  currentScreen: PAGES.DETAILS,
                },
              });
              trackEvent({
                event: {
                  category: "application",
                  action: "application-back",
                  location: "Create password",
                  label: "Back",
                },
              });
            }}
          >
            Back
          </Button>
        </Grid.Item>
      </Grid>
    </div>
  );
};

export const CreatePassword = (props: Props) => {
  usePageTracking({
    page: {
      pageType: "input-page",
      siteSection: "registration",
      siteSubSection: "",
      pageTitle: "create-password",
    },
    user: {
      authenticationStatus: "not logged in",
    },
  });

  return <CreatePasswordComponent {...props} />;
};
